import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, type AnalysisPayload, type ExtractionRow, type SystemRow, type MappingGate } from '../api/client';
import { extractPdfPages } from '../lib/pdf';

export function ProjectWorkspace() {
  const { id = '' } = useParams();
  const [data, setData] = useState<AnalysisPayload | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [source, setSource] = useState<'gemini' | 'claude' | 'sample' | null>(null);
  const [gate, setGate] = useState<MappingGate | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    try {
      const [a, m] = await Promise.all([api.getAnalysis(id), api.getMappings(id).catch(() => null)]);
      setData(a);
      setGate(m?.gate ?? null);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
  }
  useEffect(() => { void load(); }, [id]);

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true); setError(''); setSource(null);
    try {
      for (const file of Array.from(files)) {
        setStatus(`Reading ${file.name} (extracting text in your browser)…`);
        const pages = await extractPdfPages(file, (d, t, stage) =>
          setStatus(stage === 'ocr'
            ? `OCR’ing ${file.name} — page ${d}/${t} (scanned sheet, no text layer)…`
            : `Reading ${file.name} — page ${d}/${t}`));
        setStatus(`Analyzing ${file.name} (AI reads — it does not count)…`);
        const res = await api.ingest(id, { filename: file.name, pages });
        setSource(res.source);
      }
      await load();
      setStatus('Analysis complete.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ingest failed');
      setStatus('');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function toggle(sys: SystemRow) {
    const selected = !sys.selected;
    setData((d) => d && { ...d, systems: d.systems.map((s) => s.id === sys.id ? { ...s, selected: selected ? 1 : 0 } : s) });
    try { await api.toggleSystem(id, sys.id, selected); }
    catch { void load(); }
  }

  const byPrefix = (p: string) => (data?.extraction ?? []).filter((r) => r.field.startsWith(p));
  const flags = byPrefix('flag.');
  const specs = byPrefix('spec_section');
  const notes = byPrefix('note.');
  const reqs = byPrefix('requirement.');
  const scope = (k: string) => byPrefix(k);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <Link to="/" className="muted" style={{ fontSize: 13 }}>← Dashboard</Link>
          <h1 style={{ margin: '4px 0 0' }}>{data?.project?.name ?? 'Project'}</h1>
          <p className="muted" style={{ margin: 0 }}>
            {[data?.project?.county, data?.project?.state].filter(Boolean).join(', ')}
            {data?.project?.status && <> · <span className={`pill pill-${data.project.status}`}>{data.project.status}</span></>}
          </p>
        </div>
      </div>

      <div className="banner" style={{ marginTop: 14 }}>
        <strong>AI reads. Humans count.</strong> Uploading a plan set extracts and analyzes the documents —
        it identifies systems, legend symbols, specs and conflicts. It never counts symbols.
      </div>

      {/* Upload */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2>1 · Upload &amp; Analyze documents</h2>
        <input ref={fileRef} type="file" accept="application/pdf" multiple disabled={busy}
          onChange={(e) => onFiles(e.target.files)} />
        {busy && <p className="muted" style={{ marginBottom: 0 }}>{status}</p>}
        {!busy && status && <p className="muted" style={{ marginBottom: 0 }}>{status}</p>}
        {source === 'sample' && (
          <p className="muted" style={{ marginBottom: 0 }}>
            ⚠️ No Gemini key configured — used the <strong>bundled sample extraction</strong> so you can
            see the full flow. Add <code>GEMINI_API_KEYS</code> to read your real documents.
          </p>
        )}
        {data?.documents?.length ? (
          <p className="muted" style={{ marginBottom: 0 }}>Documents: {data.documents.map((d) => d.filename).join(', ')}</p>
        ) : null}
        {error && <div className="error">{error}</div>}
      </div>

      {/* Project information (auto-filled, editable — §5.3) */}
      <ProjectInfoReview projectId={id} rows={data?.extraction ?? []} onSaved={load} />

      {/* Systems */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2>2 · Systems in scope <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>— auto-selected from the documents; toggle to override</span></h2>
        {!data?.systems?.length && <p className="muted">Upload a document to auto-detect systems.</p>}
        <div className="grid cols-3">
          {data?.systems?.filter((s) => s.auto_selected || s.selected).map((s) => (
            <SystemCard key={s.id} sys={s} onToggle={() => toggle(s)} />
          ))}
        </div>
        {data?.systems?.some((s) => !s.auto_selected && !s.selected) && (
          <details style={{ marginTop: 12 }}>
            <summary className="muted">Other systems not detected ({data.systems.filter((s) => !s.auto_selected && !s.selected).length}) — add manually</summary>
            <div className="grid cols-3" style={{ marginTop: 10 }}>
              {data.systems.filter((s) => !s.auto_selected && !s.selected).map((s) => (
                <SystemCard key={s.id} sys={s} onToggle={() => toggle(s)} />
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Symbol → Device mapping checkpoint (§5.5) */}
      {gate && gate.total > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h2>3 · Symbol → Device mapping <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>— confirm a device type per symbol before BOM</span></h2>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              {gate.blocks_bom
                ? <span style={{ color: 'var(--danger)' }}><strong>{gate.needs_review}</strong> of {gate.total} symbols need a device review before BOM.</span>
                : <span><strong>All {gate.total} symbols resolved.</strong> Ready for the Count Sheet.</span>}
              <div className="muted" style={{ fontSize: 12 }}>
                {gate.confirmed} confirmed · {gate.proposed} proposed · {gate.needs_review} needs review · {gate.excluded} excluded
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, whiteSpace: 'nowrap' }}>
              <Link className="btn btn-ghost btn-sm" to={`/projects/${id}/mapping`}>Mapping review →</Link>
              <Link className="btn btn-primary btn-sm" to={`/projects/${id}/count`}
                style={gate.blocks_bom ? { opacity: 0.6 } : undefined}
                title={gate.blocks_bom ? 'Resolve flagged symbols first' : 'Generate the Symbol Quantity Excel'}>
                Count Sheet →
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        {/* Legend symbols */}
        <div className="card">
          <h2>Legend symbols <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>(read — not counted)</span></h2>
          {!data?.symbols?.length && <p className="muted">None yet.</p>}
          {data?.symbols?.length ? (
            <table>
              <thead><tr><th>Tag</th><th>Description</th><th>System</th><th>Source</th></tr></thead>
              <tbody>
                {data.symbols.map((s) => (
                  <tr key={s.id}>
                    <td><strong>{s.symbol_tag}</strong></td>
                    <td>{s.legend_description}</td>
                    <td>{s.spec_reference ?? '—'}</td>
                    <td className="muted">{safeFirst(s.sheets_present)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>

        {/* Conflicts / RFIs feed */}
        <div className="card">
          <h2>Conflicts &amp; ambiguities <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>(feed Phase 10 RFIs)</span></h2>
          {!flags.length && <p className="muted">None flagged.</p>}
          {flags.map((f, i) => (
            <div key={i} style={{ borderLeft: '3px solid var(--gold)', padding: '6px 10px', marginBottom: 8 }}>
              <div><strong>{f.field.replace('flag.', '').replace(/_/g, ' ')}</strong></div>
              <div>{f.value}</div>
              <div className="muted" style={{ fontSize: 12 }}>{f.source_ref}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 16 }}>
        <ExtractionList title="Specification sections" rows={specs} />
        <ExtractionList title="Codes & standards" rows={(data?.codes ?? []).map((c) => ({
          field: c.level, value: `${c.code} — ${c.title}${c.certainty === 'uncertain' ? '  (uncertain)' : ''}`,
          source_ref: c.source_ref, confidence: 1, status: 'auto',
        }))} />
        <ExtractionList title="Notes" rows={notes} />
        <ExtractionList title="Special requirements" rows={reqs} />
        <ExtractionList title="Alternates" rows={scope('alternate')} />
        <ExtractionList title="Allowances / Exclusions" rows={[...scope('allowance'), ...scope('exclusion')]} />
      </div>
    </div>
  );
}

function SystemCard({ sys, onToggle }: { sys: SystemRow; onToggle: () => void }) {
  const ev: string[] = (() => { try { return JSON.parse(sys.evidence ?? '[]'); } catch { return []; } })();
  return (
    <div className="card" style={{ borderColor: sys.selected ? 'var(--teal)' : undefined }}>
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!sys.selected} onChange={onToggle} style={{ marginTop: 3 }} />
        <span>
          <strong>{sys.system_name}</strong>
          <span className="muted" style={{ fontSize: 12 }}> · Div {sys.division} · {sys.system_code}</span>
          {sys.auto_selected ? (
            <div className="muted" style={{ fontSize: 12 }}>
              auto-selected · confidence {Math.round((sys.confidence ?? 0) * 100)}%
            </div>
          ) : <div className="muted" style={{ fontSize: 12 }}>not detected — manual</div>}
          {ev.slice(0, 3).map((e, i) => <div key={i} className="muted" style={{ fontSize: 11 }}>• {e}</div>)}
        </span>
      </label>
    </div>
  );
}

function ExtractionList({ title, rows }: { title: string; rows: ExtractionRow[] }) {
  if (!rows.length) return null;
  return (
    <div className="card">
      <h2>{title}</h2>
      <table>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{r.value}</td>
              <td className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{r.source_ref}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function safeFirst(json: string | null): string {
  try { const a = JSON.parse(json ?? '[]'); return Array.isArray(a) ? a[0] ?? '' : ''; } catch { return ''; }
}

// Auto-filled project info (§5.3): every field shows its source + confidence and
// is editable. Low-confidence or missing values are flagged "needs review".
const INFO_FIELDS: { key: string; label: string }[] = [
  { key: 'metadata.project_name', label: 'Project name' },
  { key: 'metadata.customer_name', label: 'Customer / Owner' },
  { key: 'metadata.gc_name', label: 'General Contractor' },
  { key: 'metadata.gc_contact', label: 'GC contact' },
  { key: 'metadata.architect', label: 'Architect' },
  { key: 'metadata.engineer', label: 'Engineer' },
  { key: 'metadata.job_address', label: 'Project address' },
  { key: 'metadata.county', label: 'County' },
  { key: 'metadata.state', label: 'State' },
  { key: 'metadata.bid_date', label: 'Bid date' },
  { key: 'metadata.bid_due_date', label: 'Bid due date' },
  { key: 'metadata.pw_jurisdiction', label: 'Prevailing-wage jurisdiction' },
  { key: 'metadata.davis_bacon', label: 'Davis-Bacon applies (yes/no)' },
];

function ProjectInfoReview({ projectId, rows, onSaved }: { projectId: string; rows: ExtractionRow[]; onSaved: () => void }) {
  const has = rows.some((r) => r.field.startsWith('metadata.'));
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2>Project information <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>— auto-filled from the documents; edit or confirm each field</span></h2>
      {!has && <p className="muted">Upload a document to auto-fill project information.</p>}
      {has && (
        <table>
          <thead><tr><th>Field</th><th>Value</th><th>Source</th><th>Confidence</th><th></th></tr></thead>
          <tbody>
            {INFO_FIELDS.map((f) => (
              <EditableField key={f.key} projectId={projectId} field={f.key} label={f.label}
                row={rows.find((r) => r.field === f.key)} onSaved={onSaved} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function EditableField({ projectId, field, label, row, onSaved }: {
  projectId: string; field: string; label: string; row: ExtractionRow | undefined; onSaved: () => void;
}) {
  const [val, setVal] = useState(row?.value ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setVal(row?.value ?? ''); setSaved(false); }, [row?.value]);

  const conf = row?.confidence ?? 0;
  const needsReview = !row || !row.value || (row.status === 'auto' && conf < 0.6);
  const dirty = (row?.value ?? '') !== val;

  async function save() {
    setSaving(true);
    try { await api.editField(projectId, { field, value: val }); setSaved(true); onSaved(); }
    catch { /* surfaced on reload */ }
    finally { setSaving(false); }
  }

  return (
    <tr style={needsReview ? { background: 'rgba(197,16,16,0.05)' } : undefined}>
      <td style={{ whiteSpace: 'nowrap' }}>
        <strong>{label}</strong>
        {needsReview && <span className="pill pill-needs_review" style={{ marginLeft: 8 }}>needs review</span>}
      </td>
      <td>
        <input value={val} onChange={(e) => { setVal(e.target.value); setSaved(false); }} placeholder="—"
          style={{ width: '100%', padding: '6px 9px', border: '1px solid var(--gray-300)', borderRadius: 6, font: 'inherit' }} />
      </td>
      <td className="muted" style={{ fontSize: 12 }}>{row?.source_ref ?? '—'}</td>
      <td className="muted" style={{ fontSize: 12 }}>{row ? (row.status !== 'auto' ? row.status : `${Math.round(conf * 100)}%`) : '—'}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {dirty ? <button className="btn btn-ghost btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          : saved ? <span className="muted" style={{ fontSize: 12 }}>saved ✓</span> : null}
      </td>
    </tr>
  );
}
